import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import BaseURL from '../assets/contants/BaseURL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faPlus, faEdit } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/header';

const UserGroup = () => {
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [meters, setMeters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [checkedItems, setCheckedItems] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const [newGroup, setNewGroup] = useState({ user: '', meters: [], main_meter: '' });
  const [editGroup, setEditGroup] = useState({ id: null, user: '', username: '', meters: [], main_meter: '' });
  const [meterSearchTerm, setMeterSearchTerm] = useState('');
  const [editMeterSearchTerm, setEditMeterSearchTerm] = useState('');

  const modalRef = useRef(null);
  const editModalRef = useRef(null);
  const deleteModalRef = useRef(null);

  useEffect(() => {
    setIsSuperuser(sessionStorage.getItem('superuser') === 'true');
    setCheckingAccess(false);
  }, []);

  const authHeaders = () => ({
    Authorization: `Token ${sessionStorage.getItem('token')}`,
  });

  const handleAuthError = (error) => {
    if (error.response && error.response.status === 401) {
      sessionStorage.removeItem('token');
      window.location.href = '/';
    } else {
      console.error('An error occurred:', error);
    }
  };

  const fetchGroups = useCallback(() => {
    setIsLoading(true);
    axios
      .get(`${BaseURL}Userauth/user-meter-groups/`, { headers: authHeaders() })
      .then((response) => {
        setGroups(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        handleAuthError(error);
        setIsLoading(false);
      });
  }, []);

  const fetchUsers = useCallback(() => {
    axios
      .get(`${BaseURL}Userauth/userdetail/`, { headers: authHeaders() })
      .then((response) => {
        setUsers(response.data.map((u) => ({
          id: u.usermod.id,
          name: `${u.usermod.first_name} ${u.usermod.last_name}`.trim() || u.usermod.email,
        })));
      })
      .catch((error) => handleAuthError(error));
  }, []);

  const fetchMeters = useCallback(() => {
    axios
      .get(`${BaseURL}meters/energymeters/`, { headers: authHeaders() })
      .then((response) => setMeters(response.data))
      .catch((error) => handleAuthError(error));
  }, []);

  useEffect(() => {
    if (!isSuperuser) return;
    fetchGroups();
    fetchUsers();
    fetchMeters();
  }, [isSuperuser, fetchGroups, fetchUsers, fetchMeters]);

  const meterMap = meters.reduce((acc, meter) => {
    acc[meter.id] = meter;
    return acc;
  }, {});

  const usersWithoutGroup = users.filter(
    (u) => !groups.some((g) => g.user_detail.id === u.id)
  );

  const filteredGroups = groups.filter((g) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const name = `${g.user_detail.first_name} ${g.user_detail.last_name}`.toLowerCase();
    return (
      name.includes(term) ||
      g.user_detail.email.toLowerCase().includes(term) ||
      g.meter_list.some((m) => m.meter_name.toLowerCase().includes(term) || m.meter_id.toLowerCase().includes(term))
    );
  });

  const handleCheckboxChange = (id) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    const updated = {};
    filteredGroups.forEach((g) => { updated[g.id] = isChecked; });
    setCheckedItems(updated);
  };

  const openAddModal = () => {
    setNewGroup({ user: '', meters: [], main_meter: '' });
    setMeterSearchTerm('');
    setModalOpen(true);
  };

  const handleAddGroup = () => {
    if (!newGroup.user || newGroup.meters.length === 0) {
      setErrorMessage('Please select a user and at least one meter.');
      setErrorModalOpen(true);
      return;
    }

    axios
      .post(
        `${BaseURL}Userauth/user-meter-groups/`,
        { user: newGroup.user, meters: newGroup.meters, main_meter: newGroup.main_meter || null },
        { headers: authHeaders() }
      )
      .then(() => {
        setModalOpen(false);
        fetchGroups();
        setSuccessMessage('User group added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        const message = data ? (data.error || data.main_meter || data.meters || data.user || 'Failed to add user group') : 'An unexpected error occurred.';
        setErrorMessage(Array.isArray(message) ? message.join('\n') : String(message));
        setErrorModalOpen(true);
      });
  };

  const handleEdit = (group) => {
    setEditGroup({
      id: group.id,
      user: group.user_detail.id,
      username: `${group.user_detail.first_name} ${group.user_detail.last_name}`.trim() || group.user_detail.email,
      meters: group.meter_list.map((m) => m.id),
      main_meter: group.main_meter_detail ? group.main_meter_detail.id : '',
    });
    setEditMeterSearchTerm('');
    setEditModalOpen(true);
  };

  const handleUpdateGroup = () => {
    if (editGroup.meters.length === 0) {
      setErrorMessage('Please select at least one meter.');
      setErrorModalOpen(true);
      return;
    }

    axios
      .put(
        `${BaseURL}Userauth/user-meter-groups/${editGroup.id}/`,
        { user: editGroup.user, meters: editGroup.meters, main_meter: editGroup.main_meter || null },
        { headers: authHeaders() }
      )
      .then(() => {
        setEditModalOpen(false);
        fetchGroups();
        setSuccessMessage('User group updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        const message = data ? (data.error || data.main_meter || data.meters || 'Failed to update user group') : 'An unexpected error occurred.';
        setErrorMessage(Array.isArray(message) ? message.join('\n') : String(message));
        setErrorModalOpen(true);
      });
  };

  const handleDeleteGroups = async () => {
    const idsToDelete = Object.keys(checkedItems).filter((id) => checkedItems[id]);
    try {
      for (const id of idsToDelete) {
        await axios.delete(`${BaseURL}Userauth/user-meter-groups/${id}/`, { headers: authHeaders() });
      }
      setCheckedItems({});
      fetchGroups();
      setSuccessMessage('Selected user groups deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setDeleteModalOpen(false);
    }
  };

  const renderMeterPicker = (availableSearchTerm, setAvailableSearchTerm, selectedIds, onAdd, onRemove) => {
    const term = availableSearchTerm.toLowerCase();
    const available = meters.filter(
      (m) => m.meter_name.toLowerCase().includes(term) || m.meter_id.toLowerCase().includes(term)
    );

    return (
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold', fontSize: '14px' }}>
            Available Meters
          </label>
          <div style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '8px', height: '230px', overflowY: 'auto', boxSizing: 'border-box' }}>
            <input
              type="text"
              placeholder="Search meters..."
              value={availableSearchTerm}
              onChange={(e) => setAvailableSearchTerm(e.target.value)}
              style={{ width: '90%', padding: '5px', border: '1px solid #ccc', borderRadius: '5px', fontSize: '12px', marginBottom: '10px' }}
            />
            {available.map((meter) => {
              const isAdded = selectedIds.includes(meter.id);
              return (
                <div
                  key={meter.id}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', backgroundColor: '#e0e0e0', padding: '5px', borderRadius: '5px', fontSize: '12px' }}
                >
                  <span style={{ color: isAdded ? 'gray' : 'black' }}>{meter.meter_id} - {meter.meter_name}</span>
                  <button
                    onClick={() => onAdd(meter.id)}
                    disabled={isAdded}
                    style={{ backgroundColor: isAdded ? 'gray' : '#4CAF50', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: isAdded ? 'not-allowed' : 'pointer', fontSize: '10px' }}
                  >
                    {isAdded ? 'Added' : 'Add'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold', fontSize: '14px' }}>
            Added Meters
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', backgroundColor: '#f9f9f9', height: '230px', overflowY: 'auto', boxSizing: 'border-box' }}>
            {selectedIds.length === 0 && <span style={{ fontSize: '12px', color: '#888' }}>No meters selected.</span>}
            {selectedIds.map((meterId) => (
              <div
                key={meterId}
                style={{ backgroundColor: '#e0e0e0', marginBottom: '5px', padding: '5px', borderRadius: '3px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}
              >
                <span>{meterMap[meterId]?.meter_id} - {meterMap[meterId]?.meter_name}</span>
                <button
                  onClick={() => onRemove(meterId)}
                  style={{ backgroundColor: 'red', color: 'white', border: 'none', padding: '5px', borderRadius: '5px', cursor: 'pointer', fontSize: '10px' }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const ErrorModal = () => (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)', display: errorModalOpen ? 'flex' : 'none',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000,
      }}
    >
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', width: '300px', textAlign: 'center' }}>
        <h3 style={{ color: 'red', marginBottom: '15px' }}>Error</h3>
        <p style={{ marginBottom: '20px', whiteSpace: 'pre-line' }}>{errorMessage}</p>
        <button
          style={{ padding: '10px 15px', backgroundColor: '#007acc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          onClick={() => setErrorModalOpen(false)}
        >
          Close
        </button>
      </div>
    </div>
  );

  if (checkingAccess) {
    return <div>Loading...</div>;
  }

  if (!isSuperuser) {
    return (
      <div style={{ textAlign: 'center', width: '100%' }}>
        <div style={{ backgroundColor: '#007acc', color: 'white', alignContent: 'center', fontSize: '14px', height: '40px' }}>
          <Header title="USER GROUP" />
        </div>
        <div style={{ marginTop: '40px', fontSize: '16px', color: '#f44336' }}>
          You do not have permission to view this page.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ textAlign: 'center', width: '100%' }}>
      <div style={{ backgroundColor: '#007acc', color: 'white', alignContent: 'center', fontSize: '14px', height: '40px' }}>
        <Header title="USER GROUP" />
      </div>

      <div style={{ border: '1px solid #ccc', padding: '10px', marginTop: '20px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'white', margin: '20px auto', maxWidth: '1000px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '30%' }}>
            <input
              type="text"
              placeholder="Search"
              style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '8px', flex: '1', marginRight: '10px' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FontAwesomeIcon icon={faSearch} style={{ color: '#007acc' }} />
          </div>

          <div style={{ marginLeft: 'auto' }}>
            <button
              style={{ padding: '10px 15px', marginRight: '10px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              onClick={() => {
                const anySelected = Object.values(checkedItems).includes(true);
                if (anySelected) setDeleteModalOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faTrash} style={{ marginRight: '5px' }} />
              Delete
            </button>
            <button
              style={{ padding: '10px 15px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              onClick={openAddModal}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} />
              Add
            </button>
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', margin: '20px 0' }}>
        {successMessage && (
          <div style={{ padding: '10px', borderRadius: '5px', backgroundColor: '#4CAF50', color: 'white', margin: '10px auto', width: 'fit-content' }}>
            {successMessage}
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px', padding: '10px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'white', maxWidth: '1000px', margin: '20px auto' }}>
        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#007acc', color: 'white' }}>
            <tr>
              <th style={{ padding: '10px' }}>
                <input type="checkbox" onChange={handleSelectAll} />
              </th>
              <th style={{ padding: '10px' }}>S.No</th>
              <th style={{ padding: '10px' }}>User</th>
              <th style={{ padding: '10px' }}>Meters</th>
              <th style={{ padding: '10px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.length > 0 ? (
              filteredGroups.map((group, index) => (
                <tr key={group.id} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                  <td style={{ padding: '10px' }}>
                    <input type="checkbox" checked={!!checkedItems[group.id]} onChange={() => handleCheckboxChange(group.id)} />
                  </td>
                  <td style={{ padding: '10px' }}>{index + 1}</td>
                  <td style={{ padding: '10px' }}>
                    {`${group.user_detail.first_name} ${group.user_detail.last_name}`.trim() || group.user_detail.email}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'left' }}>
                    {group.meter_list.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {group.meter_list.map((m) => (
                          <span key={m.id}>
                            {m.meter_id} - {m.meter_name}
                            {group.main_meter_detail && group.main_meter_detail.id === m.id && (
                              <span style={{ backgroundColor: '#007acc', color: 'white', padding: '2px 6px', borderRadius: '8px', fontSize: '10px', marginLeft: '6px' }}>
                                MAIN
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '10px', color: 'blue', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} onClick={() => handleEdit(group)} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ padding: '10px', textAlign: 'center' }}>
                  No user groups found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div ref={modalRef} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '600px', width: '100%', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ marginBottom: '10px' }}>Add User Group</h3>

            <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold', fontSize: '14px' }}>User</label>
            <select
              value={newGroup.user}
              onChange={(e) => setNewGroup({ ...newGroup, user: e.target.value })}
              style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '8px', marginBottom: '15px', boxSizing: 'border-box' }}
            >
              <option value="">-- Select a user --</option>
              {usersWithoutGroup.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>

            {renderMeterPicker(
              meterSearchTerm,
              setMeterSearchTerm,
              newGroup.meters,
              (meterId) => setNewGroup((prev) => (prev.meters.includes(meterId) ? prev : { ...prev, meters: [...prev.meters, meterId] })),
              (meterId) => setNewGroup((prev) => ({
                ...prev,
                meters: prev.meters.filter((id) => id !== meterId),
                main_meter: prev.main_meter === meterId ? '' : prev.main_meter,
              }))
            )}

            <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold', fontSize: '14px' }}>Main Meter</label>
            <select
              value={newGroup.main_meter}
              onChange={(e) => setNewGroup({ ...newGroup, main_meter: e.target.value })}
              disabled={newGroup.meters.length === 0}
              style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '8px', marginBottom: '20px', boxSizing: 'border-box' }}
            >
              <option value="">-- No main meter --</option>
              {newGroup.meters.map((meterId) => (
                <option key={meterId} value={meterId}>
                  {meterMap[meterId]?.meter_id} - {meterMap[meterId]?.meter_name}
                </option>
              ))}
            </select>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={handleAddGroup} style={{ backgroundColor: '#4CAF50', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', flex: 1, marginRight: '10px' }}>
                Add Group
              </button>
              <button onClick={() => setModalOpen(false)} style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div ref={editModalRef} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '600px', width: '100%', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ marginBottom: '10px' }}>Edit User Group</h3>

            <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold', fontSize: '14px' }}>User</label>
            <input
              type="text"
              value={editGroup.username}
              readOnly
              style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '8px', marginBottom: '15px', backgroundColor: '#f0f0f0', boxSizing: 'border-box' }}
            />

            {renderMeterPicker(
              editMeterSearchTerm,
              setEditMeterSearchTerm,
              editGroup.meters,
              (meterId) => setEditGroup((prev) => (prev.meters.includes(meterId) ? prev : { ...prev, meters: [...prev.meters, meterId] })),
              (meterId) => setEditGroup((prev) => ({
                ...prev,
                meters: prev.meters.filter((id) => id !== meterId),
                main_meter: prev.main_meter === meterId ? '' : prev.main_meter,
              }))
            )}

            <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold', fontSize: '14px' }}>Main Meter</label>
            <select
              value={editGroup.main_meter}
              onChange={(e) => setEditGroup({ ...editGroup, main_meter: e.target.value })}
              disabled={editGroup.meters.length === 0}
              style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '8px', marginBottom: '20px', boxSizing: 'border-box' }}
            >
              <option value="">-- No main meter --</option>
              {editGroup.meters.map((meterId) => (
                <option key={meterId} value={meterId}>
                  {meterMap[meterId]?.meter_id} - {meterMap[meterId]?.meter_name}
                </option>
              ))}
            </select>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={handleUpdateGroup} style={{ backgroundColor: '#4CAF50', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', flex: 1, marginRight: '10px' }}>
                Update Group
              </button>
              <button onClick={() => setEditModalOpen(false)} style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div ref={deleteModalRef} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', width: '400px', textAlign: 'center', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)' }}>
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete the selected user groups?</p>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-around' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={{ padding: '10px 15px', color: 'white', backgroundColor: '#4CAF50', border: 'none', borderRadius: '8px', cursor: 'pointer' }} onClick={handleDeleteGroups}>
                  Yes, Delete
                </button>
                <button style={{ padding: '10px 15px', color: 'white', backgroundColor: '#f44336', border: 'none', borderRadius: '8px', cursor: 'pointer' }} onClick={() => setDeleteModalOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ErrorModal />
    </div>
  );
};

export default UserGroup;
